/**
 * All Transactions Page - Raw Material Transactions
 * Shows raw materials sent to manufacturing units with payment tracking
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  Weight,
  Package,
  CheckCircle,
  AlertCircle,
  Clock,
  X,
  Download,
  Search,
  Coins,
  BarChart3,
  ChevronDown,
  CreditCard,
  Loader2,
  User,
  Truck,
  MapPin,
  Route
} from 'lucide-react';
import { BankAccountDisplay } from '@/components/gaushala/BankAccountDisplay';
import { BankAccountDialog } from '@/components/gaushala/BankAccountDialog';
import { BankAccountResponse } from '@/services/gaushala/bankAccountService';
import { transportApi, GaushalaPickupRoute } from '@/services/transport/api';

interface RawMaterialTransaction {
  id: number;
  batchCode: string;
  manufacturingUnitId: number;
  quantityKg: number;
  quantityAvailableKg: number;
  consumedQuantityKg: number;
  status: string;
  notes?: string;
  rawMaterialTypeId: number;
  rawMaterialTypeName: string;
  dungTypeId?: number;
  dungTypeName?: string;
  payoutAmount?: number;
  payoutCurrency?: string;
  payoutStatus?: string;
  payoutGatewayType?: string;
  // Driver and Vehicle info (from transport service - may be null)
  driverName?: string;
  driverId?: number;
  vehicleNumber?: string;
  vehicleId?: number;
  createdAt: string;
  updatedAt: string;
}

interface RawMaterialTransactionDetail extends RawMaterialTransaction {
  payoutInitiatedAt?: string;
  payoutCompletedAt?: string;
  payoutInitiatedBy?: number;
  payoutFailureReason?: string;
  timeline: TimelineEntry[];
  qc?: QCInfo;
  // Extended driver/vehicle info for detail view
  driverPhone?: string;
  vehicleType?: string;
  // Route/Delivery info
  routeId?: number;
  routeCode?: string;
  routeName?: string;
  pickupTime?: string;
  pickupCompletedAt?: string;
}

interface TimelineEntry {
  id: number;
  previousStatus: string;
  newStatus: string;
  userId: number;
  notes?: string;
  createdAt: string;
}

interface QCInfo {
  id: number;
  moisturePercent: number;
  purityPercent: number;
  grade: string;
  priceMultiplier: number;
  verifiedBy: number;
  verifiedAt: string;
  notes?: string;
  createdAt: string;
}

interface TransactionSummary {
  totalTransactions: number;
  approvedCount: number;
  pendingCount: number;
  consumedCount: number;
  totalQuantityKg: number;
  totalConsumedKg: number;
}

interface Props {
  onClose?: () => void;
}

const translations = {
  en: {
    title: 'Raw Material Transactions',
    subtitle: 'Track raw materials sent to manufacturing units',
    summary: 'Summary',
    totalTransactions: 'Total Transactions',
    totalRawMaterial: 'Total Raw Material',
    approvedMaterial: 'Approved Material',
    pendingApproval: 'Pending Approval',
    searchPlaceholder: 'Search by batch code, material type, date, vehicle, driver, quantity, amount, or remaining...',
    filterByStatus: 'Filter by Status',
    allStatus: 'All Status',
    batchCode: 'Batch Code',
    materialType: 'Material Type',
    quantity: 'Quantity',
    available: 'Available',
    consumed: 'Consumed',
    status: 'Status',
    date: 'Date',
    actions: 'Actions',
    viewDetails: 'View Details',
    kg: 'kg',
    noTransactions: 'No transactions found',
    close: 'Close',
    exportData: 'Export Data',
    initialized: 'Initialized',
    qcPending: 'QC Pending',
    qcVerified: 'QC Verified',
    approved: 'Approved',
    readyForProduction: 'Ready for Production',
    consumedStatus: 'Consumed',
    qualityInfo: 'Quality Information',
    grade: 'Grade',
    moisture: 'Moisture',
    purity: 'Purity',
    priceMultiplier: 'Price Multiplier',
    payment: 'Payment Information',
    payoutAmount: 'Payout Amount',
    payoutStatus: 'Payment Status',
    paymentGateway: 'Payment Gateway',
    initiatedAt: 'Payment Initiated',
    completedAt: 'Payment Completed',
    failureReason: 'Failure Reason',
    notPaid: 'Payment Pending',
    driver: 'Driver',
    vehicle: 'Vehicle',
    notAssigned: 'Not Assigned',
    deliveryInfo: 'Delivery Information',
    routeCode: 'Route Code',
    routeName: 'Route',
    pickupTime: 'Pickup Time',
    pickupLocation: 'Pickup Location',
    expectedQuantity: 'Expected Quantity',
    scheduleType: 'Schedule Type'
  }
};

export default function AllTransactions({ onClose }: Props) {
  const [transactions, setTransactions] = useState<RawMaterialTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<RawMaterialTransactionDetail | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [bankDialogMode, setBankDialogMode] = useState<'create' | 'edit'>('create');
  const [bankAccount, setBankAccount] = useState<BankAccountResponse | null>(null);
  const [pickupRoutes, setPickupRoutes] = useState<GaushalaPickupRoute[]>([]);
  const [pickupRoutesLoading, setPickupRoutesLoading] = useState(false);

  // Use relative path in dev mode for Vite proxy
  const isDev = import.meta.env.DEV;
  const API_BASE = isDev ? '/gaushala-service' : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

  useEffect(() => {
    fetchTransactions();
    fetchSummary();
    fetchBankAccount();
    fetchPickupRoutes();
  }, [filterStatus, page]);

  const fetchBankAccount = async () => {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      if (!token) return;

      const response = await fetch(
        `${API_BASE}/api/v1/gaushala/bank-accounts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setBankAccount(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching bank account:', error);
    }
  };

  const fetchPickupRoutes = async () => {
    try {
      setPickupRoutesLoading(true);
      const token = localStorage.getItem('saubhagya_jwt_token');
      if (!token) return;

      // Extract gaushalaId from user context or API
      const response = await fetch(`${API_BASE}/api/v1/gaushala/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const gaushalaId = data.data?.gaushalaDetails?.id;
        if (gaushalaId) {
          const routes = await transportApi.getGaushalaPickupRoutes(gaushalaId);
          setPickupRoutes(routes);
        }
      }
    } catch (error) {
      console.error('Error fetching pickup routes:', error);
    } finally {
      setPickupRoutesLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      params.append('page', page.toString());
      params.append('size', '10');

      const token = localStorage.getItem('saubhagya_jwt_token');
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_BASE}/api/v1/raw-material-transactions?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.data?.content || []);
        setTotalPages(data.data?.totalPages || 0);
      } else if (response.status === 401) {
        console.error('Unauthorized: Invalid or expired token');
      } else if (response.status === 403) {
        console.error('Forbidden: No access to gaushala transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(
        `${API_BASE}/api/v1/raw-material-transactions/summary`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSummary(data.data);
      } else if (response.status === 401) {
        console.error('Unauthorized: Invalid or expired token');
      } else if (response.status === 403) {
        console.error('Forbidden: No access to gaushala transactions');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchTransactionDetails = async (transactionId: number) => {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(
        `${API_BASE}/api/v1/raw-material-transactions/${transactionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedTransaction(data.data);
        setShowDetails(true);
      } else if (response.status === 401) {
        console.error('Unauthorized: Invalid or expired token');
      } else if (response.status === 403) {
        console.error('Forbidden: No access to this transaction');
      } else if (response.status === 404) {
        console.error('Transaction not found');
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'INITIALIZED': 'bg-gray-100 text-gray-800',
      'QC_PENDING': 'bg-yellow-100 text-yellow-800',
      'QC_VERIFIED': 'bg-blue-100 text-blue-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'READY_FOR_PRODUCTION': 'bg-indigo-100 text-indigo-800',
      'CONSUMED': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'INITIALIZED': translations.en.initialized,
      'QC_PENDING': translations.en.qcPending,
      'QC_VERIFIED': translations.en.qcVerified,
      'APPROVED': translations.en.approved,
      'READY_FOR_PRODUCTION': translations.en.readyForProduction,
      'CONSUMED': translations.en.consumedStatus
    };
    return labels[status] || status;
  };

  const getPaymentDisplayStatus = (transactionStatus: string) => {
    // Show as Approved if status is QC_VERIFIED or beyond (APPROVED, READY_FOR_PRODUCTION, CONSUMED)
    const approvedStatuses = ['QC_VERIFIED', 'APPROVED', 'READY_FOR_PRODUCTION', 'CONSUMED'];
    return approvedStatuses.includes(transactionStatus) ? 'APPROVED' : 'INITIALIZED';
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const dateStr = new Date(tx.createdAt).toLocaleDateString().toLowerCase();

    const quantityStr = tx.quantityKg != null ? tx.quantityKg.toString() : '';
    const remainingStr = tx.quantityAvailableKg != null ? tx.quantityAvailableKg.toString() : '';
    const consumedStr = tx.consumedQuantityKg != null ? tx.consumedQuantityKg.toString() : '';
    const amountStr = tx.payoutAmount != null ? tx.payoutAmount.toString() : '';
    const amountFormatted = tx.payoutAmount != null ? tx.payoutAmount.toFixed(2) : '';
    const currency = (tx.payoutCurrency || 'INR').toLowerCase();
    const statusLabel = getStatusLabel(tx.status).toLowerCase();

    return (
      (tx.batchCode || '').toLowerCase().includes(term) ||
      (tx.rawMaterialTypeName || '').toLowerCase().includes(term) ||
      dateStr.includes(term) ||
      (tx.vehicleNumber || '').toLowerCase().includes(term) ||
      (tx.driverName || '').toLowerCase().includes(term) ||
      quantityStr.includes(term) ||
      remainingStr.includes(term) ||
      consumedStr.includes(term) ||
      amountStr.includes(term) ||
      amountFormatted.includes(term) ||
      currency.includes(term) ||
      statusLabel.includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{translations.en.title}</h1>
              <p className="text-gray-600 mt-2">{translations.en.subtitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  setBankDialogMode('create');
                  setShowBankDialog(true);
                }}
                className="gap-2"
                variant={bankAccount ? "outline" : "default"}
              >
                <CreditCard className="w-4 h-4" />
                {bankAccount ? "Add New Bank Account" : "Link Bank Account"}
              </Button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bank Account Dialog
            - mode="create": empty fields (top "Add New Bank Account" button); replaces existing via PUT if one exists
            - mode="edit":   pre-filled with current account (card "Edit" button); partial update via PUT */}
        <BankAccountDialog
          isOpen={showBankDialog}
          onOpenChange={setShowBankDialog}
          mode={bankDialogMode}
          existingAccount={bankAccount || undefined}
          onSuccess={(account) => {
            setBankAccount(account);
            setShowBankDialog(false);
          }}
        />

        {/* Bank Account Status Card */}
        {bankAccount ? (
          <div className="bg-white rounded-lg shadow p-4 mb-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Bank Account Linked</p>
                  <p className="text-base font-semibold text-gray-900">
                    {bankAccount.bankName} • {bankAccount.maskedAccountNumber}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{bankAccount.accountType || 'Account'}</span>
                    {bankAccount.isVerified ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">✓ Verified</span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">⚠ Pending Verification</span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                onClick={() => {
                  setBankDialogMode('edit');
                  setShowBankDialog(true);
                }}
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-800"
              >
                Edit
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 rounded-lg shadow p-4 mb-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-blue-600 font-medium">No Bank Account Linked</p>
                  <p className="text-sm text-blue-500 mt-1">Link your bank account to enable payment processing for transactions</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setBankDialogMode('create');
                  setShowBankDialog(true);
                }}
                className="gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Link Account
              </Button>
            </div>
          </div>
        )}

        {/* Scheduled Pickups from Transport */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 border-l-4 border-blue-500">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Scheduled Pickups</h3>
          {pickupRoutesLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-600">Loading pickup schedules...</span>
            </div>
          ) : pickupRoutes.length === 0 ? (
            <p className="text-sm text-gray-500">No scheduled pickups for this gaushala</p>
          ) : (
            <div className="space-y-3">
              {pickupRoutes.map((pickup) => (
                <div key={`${pickup.routeId}-${pickup.sequenceOrder}`} className="flex items-start justify-between rounded-lg border border-gray-200 p-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{pickup.routeCode}</p>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        pickup.routeStatusCode === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                        pickup.routeStatusCode === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                        pickup.routeStatusCode === 'PAUSED' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {pickup.routeStatusName}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{pickup.routeName}</p>
                    {pickup.expectedQuantityMinKg && pickup.expectedQuantityMaxKg && (
                      <p className="text-sm text-gray-600">Expected: {pickup.expectedQuantityMinKg} - {pickup.expectedQuantityMaxKg} kg</p>
                    )}
                    {/* Display schedule date if available */}
                    {pickup.scheduleDate && (
                      <p className="text-sm text-gray-700">
                        Date: {new Date(pickup.scheduleDate).toLocaleDateString('en-IN', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                    {pickup.pickupTime && (
                      <p className="text-sm font-medium text-blue-600">Pickup: {pickup.pickupTime}</p>
                    )}

                    {/* Driver Status Badge - Real-time tracking */}
                    {pickup.executionId && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {pickup.partitionLabel && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                            <Truck className="h-3 w-3" />
                            {pickup.partitionLabel}
                          </span>
                        )}
                        {pickup.currentStopStatus === 'COMPLETED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                            <CheckCircle className="h-3 w-3" />
                            Collection completed
                            {pickup.quantityCollectedKg && (
                              <span className="ml-1 text-gray-500">({pickup.quantityCollectedKg} kg)</span>
                            )}
                          </span>
                        ) : pickup.currentStopStatus === 'ARRIVED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                            <MapPin className="h-3 w-3" />
                            Driver arrived
                            {pickup.driverArrivedAt && (
                              <span className="ml-1 text-green-600">
                                at {new Date(pickup.driverArrivedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </span>
                        ) : pickup.currentStopStatus === 'SKIPPED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-700">
                            <X className="h-3 w-3" />
                            Skipped
                          </span>
                        ) : (pickup.executionStatus === 'STARTED' || pickup.executionStatus === 'IN_PROGRESS') ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                            <Truck className="h-3 w-3 animate-pulse" />
                            Driver en route
                            {pickup.currentStopIndex && pickup.totalStops && (
                              <span className="ml-1 text-blue-600">(at stop {pickup.currentStopIndex} of {pickup.totalStops})</span>
                            )}
                          </span>
                        ) : pickup.executionStatus === 'SCHEDULED' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-700">
                            <Clock className="h-3 w-3" />
                            Scheduled for today
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    {pickup.assignedDriverName && (
                      <p className="text-gray-700">Driver: {pickup.assignedDriverName}</p>
                    )}
                    {pickup.assignedVehicleNumber && (
                      <p className="text-gray-500">Vehicle: {pickup.assignedVehicleNumber}</p>
                    )}
                    {pickup.scheduleTypeName && (
                      <p className="text-xs text-gray-400">{pickup.scheduleTypeName}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{translations.en.totalTransactions}</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.totalTransactions}</p>
                </div>
                <Package className="w-10 h-10 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{translations.en.totalRawMaterial}</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.totalQuantityKg.toFixed(0)}</p>
                  <p className="text-gray-500 text-xs">{translations.en.kg}</p>
                </div>
                <Weight className="w-10 h-10 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{translations.en.approvedMaterial}</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.approvedCount}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{translations.en.pendingApproval}</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.pendingCount}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-yellow-500" />
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={translations.en.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading transactions...</div>
          ) : filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.batchCode}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.materialType}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.quantity}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.driver}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.vehicle}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.payoutAmount}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.date}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">{translations.en.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{tx.batchCode}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{tx.rawMaterialTypeName}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Weight className="w-4 h-4 text-gray-400" />
                          {tx.quantityKg} {translations.en.kg}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {tx.driverName ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="text-gray-700">{tx.driverName}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">{translations.en.notAssigned}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {tx.vehicleNumber ? (
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-green-500" />
                            <span className="text-gray-700">{tx.vehicleNumber}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">{translations.en.notAssigned}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {tx.payoutAmount ? (
                          <div className="flex items-center gap-1">
                            <Coins className="w-4 h-4 text-green-600" />
                            <span className="font-semibold text-green-700">
                              {tx.payoutAmount.toFixed(2)} {tx.payoutCurrency || 'INR'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">{translations.en.notPaid}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => fetchTransactionDetails(tx.id)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {translations.en.viewDetails}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">{translations.en.noTransactions}</div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Transaction Details Modal */}
        {showDetails && selectedTransaction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold">{selectedTransaction.batchCode}</h2>
                  <p className="text-blue-100 mt-1">Transaction ID: {selectedTransaction.id}</p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-white hover:bg-blue-800 rounded-full p-2 transition"
                >
                  ✕
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="overflow-y-auto flex-1 p-8 space-y-6">

                {/* Section 1: Basic Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Transaction Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.materialType}</p>
                      <p className="text-gray-900 font-bold text-lg mt-2">{selectedTransaction.rawMaterialTypeName}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.status}</p>
                      <div className="mt-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedTransaction.status)}`}>
                          {getStatusLabel(selectedTransaction.status)}
                        </span>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.date}</p>
                      <p className="text-gray-900 font-bold text-lg mt-2">{new Date(selectedTransaction.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Delivery Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-cyan-600" />
                    {translations.en.deliveryInfo}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.driver}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <User className="w-5 h-5 text-cyan-600" />
                        <div>
                          <p className="text-gray-900 font-bold text-lg">
                            {selectedTransaction.driverName || translations.en.notAssigned}
                          </p>
                          {selectedTransaction.driverPhone && (
                            <p className="text-gray-600 text-sm">{selectedTransaction.driverPhone}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.vehicle}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Truck className="w-5 h-5 text-cyan-600" />
                        <div>
                          <p className="text-gray-900 font-bold text-lg">
                            {selectedTransaction.vehicleNumber || translations.en.notAssigned}
                          </p>
                          {selectedTransaction.vehicleType && (
                            <p className="text-gray-600 text-sm">{selectedTransaction.vehicleType}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {selectedTransaction.routeCode && (
                      <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.routeCode}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Route className="w-5 h-5 text-cyan-600" />
                          <div>
                            <p className="text-gray-900 font-bold text-lg">{selectedTransaction.routeCode}</p>
                            {selectedTransaction.routeName && (
                              <p className="text-gray-600 text-sm">{selectedTransaction.routeName}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedTransaction.pickupTime && (
                      <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.pickupTime}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-5 h-5 text-cyan-600" />
                          <p className="text-gray-900 font-bold text-lg">{selectedTransaction.pickupTime}</p>
                        </div>
                      </div>
                    )}
                    {selectedTransaction.pickupCompletedAt && (
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200 md:col-span-2">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">Pickup Completed</p>
                        <div className="flex items-center gap-2 mt-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <p className="text-gray-900 font-bold text-lg">
                            {new Date(selectedTransaction.pickupCompletedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 3: Quantity */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Weight className="w-5 h-5 text-green-600" />
                    Quantity
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-5 border-l-4 border-green-500">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">Total Quantity</p>
                      <p className="text-3xl font-bold text-green-700 mt-2">{selectedTransaction.quantityKg}</p>
                      <p className="text-gray-600 text-sm mt-1">{translations.en.kg}</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-5 border-l-4 border-yellow-500">
                      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">Available Quantity</p>
                      <p className="text-3xl font-bold text-yellow-700 mt-2">{selectedTransaction.quantityAvailableKg}</p>
                      <p className="text-gray-600 text-sm mt-1">{translations.en.kg}</p>
                    </div>
                  </div>
                </div>

                {/* Section 4: Quality Information */}
                {selectedTransaction.qc && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600" />
                      Quality Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.grade}</p>
                        <p className="text-2xl font-bold text-orange-700 mt-2">{selectedTransaction.qc.grade}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.priceMultiplier}</p>
                        <p className="text-2xl font-bold text-orange-700 mt-2">{selectedTransaction.qc.priceMultiplier}x</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.moisture}</p>
                        <p className="text-2xl font-bold text-indigo-700 mt-2">{selectedTransaction.qc.moisturePercent}%</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.purity}</p>
                        <p className="text-2xl font-bold text-indigo-700 mt-2">{selectedTransaction.qc.purityPercent}%</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 5: Payment Information */}
                {selectedTransaction.payoutAmount && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-emerald-600" />
                      {translations.en.payment}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.payoutAmount}</p>
                        <p className="text-3xl font-bold text-emerald-700 mt-2">
                          {selectedTransaction.payoutAmount?.toFixed(2)} {selectedTransaction.payoutCurrency || 'INR'}
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.payoutStatus}</p>
                        <div className="mt-2">
                          {getPaymentDisplayStatus(selectedTransaction.status) === 'APPROVED' && (
                            <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800">
                              Approved
                            </span>
                          )}
                          {getPaymentDisplayStatus(selectedTransaction.status) === 'INITIALIZED' && (
                            <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800">
                              Initialized
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.paymentGateway}</p>
                        <p className="text-lg font-bold text-emerald-700 mt-2">{selectedTransaction.payoutGatewayType || '-'}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                        <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.initiatedAt}</p>
                        <p className="text-lg font-bold text-emerald-700 mt-2">
                          {selectedTransaction.payoutInitiatedAt ? new Date(selectedTransaction.payoutInitiatedAt).toLocaleString() : '-'}
                        </p>
                      </div>
                      {selectedTransaction.payoutCompletedAt && (
                        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                          <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.completedAt}</p>
                          <p className="text-lg font-bold text-emerald-700 mt-2">
                            {new Date(selectedTransaction.payoutCompletedAt).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedTransaction.payoutFailureReason && (
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200 md:col-span-2">
                          <p className="text-gray-600 text-xs font-semibold uppercase tracking-wider">{translations.en.failureReason}</p>
                          <p className="text-gray-800 mt-2">{selectedTransaction.payoutFailureReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 6: Additional Notes */}
                {selectedTransaction.notes && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      Notes
                    </h3>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-gray-800">{selectedTransaction.notes}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t px-8 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
